import { useCallback, useEffect, useState } from 'react'
import L from 'leaflet'
import {
    deleteMapLayer,
    isAlreadyMapLayerAdded,
    isDevicePinMovedAfterLastHeartbeat
} from '../../../utils/floor-plan-layer'

const connectivityStrength = {
    excellent: 'excellent',
    good: 'good',
    bad: 'bad',
    low: 'low',
    unknown: 'unknown'
}

const connectivityStrengthGradient = {
    excellent: { 0.1: '#FFFFFF', 0.85: '#5CC0FF', 1: '#5CC0FF' },
    good: { 0.1: '#FFFFFF', 0.85: '#66FF99', 1: '#66FF99' },
    bad: { 0.1: '#FFFFFF', 0.77: '#FF5454', 1: '#FF5454' },
    low: { 0.1: '#FFFFFF', 0.74: '#FFB241', 1: '#FF9800' },
    unknown: { 0.1: '#FFFFFF', 0.85: '#D4D4D4', 1: '#D4D4D4' }
}

///initState
// e.g. { excellent: [], good: [], bad: [] }
const createInitState = connectivityStrengths =>
    Object.values(connectivityStrengths).reduce(
        (acc, current) => ({
            ...acc,
            [current]: []
        }),
        {}
    )

export const useConnectivityHeatmap = (
    devices,
    map,
    isSelectedConnectivity = true
) => {
    const [connectivityHeatmapPoints, setConnectivityHeatmapPoints] = useState(
        createInitState(connectivityStrength)
    )

    const connectivityStrengths = Object.keys(connectivityStrength)

    useEffect(() => {
        if (devices) {
            let newConnectivityHeatmapPoints = {}
            for (const strength of connectivityStrengths) {
                newConnectivityHeatmapPoints = {
                    ...newConnectivityHeatmapPoints,
                    [strength]: calculateCoordinatesByStrength(
                        strength,
                        devices
                    )
                }
            }
            setConnectivityHeatmapPoints(newConnectivityHeatmapPoints)

            if (isAlreadyMapLayerAdded(map) && isSelectedConnectivity) {
                reloadConnectivityHeatmap(newConnectivityHeatmapPoints)
            }
        }
    }, [devices, map])

    const calculateCoordinatesByStrength = (strength, devices) => {
        const isUnknownStrength = strength === 'unknown'

        const filteredDevices = devices.filter(device => {
            const meetsConnectionCriteria =
                device.connection_strength === connectivityStrength[strength]

            const meetsPinMovedCriteria = isDevicePinMovedAfterLastHeartbeat(
                device
            )

            if (isUnknownStrength) {
                return meetsConnectionCriteria || meetsPinMovedCriteria
            } else {
                return meetsConnectionCriteria && !meetsPinMovedCriteria
            }
        })

        return filteredDevices
            .filter(device => !!device.coordinates)
            .map(device => [device.coordinates.lat, device.coordinates.lng])
    }

    const createMapLayer = (strength, connectivityHeatmapPoints) => {
        return L.heatLayer(connectivityHeatmapPoints[strength], {
            minOpacity: 0.9,
            radius: 50,
            blur: 15,
            gradient: connectivityStrengthGradient[strength]
        })
    }

    const addConnectivityHeatmap = useCallback(() => {
        if (!map) return
        for (const strength of connectivityStrengths) {
            createMapLayer(strength, connectivityHeatmapPoints).addTo(map)
        }
    }, [connectivityHeatmapPoints, map])

    const reloadConnectivityHeatmap = useCallback(
        connectivityHeatmapPoints => {
            if (!map) return
            deleteMapLayer(map)
            for (const strength of connectivityStrengths) {
                createMapLayer(strength, connectivityHeatmapPoints).addTo(map)
            }
        },
        [map]
    )

    return { addConnectivityHeatmap }
}
